import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { EaDisplay } from '@app/components/pr-display';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrModalConfirmComponent } from '@app/components/pr-modal/pr-modal-confirm/pr-modal-confirm.component';
import { ParrainageService } from '@app/dashboard/parrainage/parrainage.service';
import {
  PledgeRead,
  Product,
} from '@app/forms/subscription/definition/interface/Pledge';
import { ProductsType } from '@app/providers/products.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@UntilDestroy()
@Component({
    selector: 'discount-form',
    templateUrl: './discount-form.component.html',
    styleUrls: ['./discount-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiscountFormComponent
  extends EaSubscriptionBuilder
  implements OnInit, AfterViewInit
{
  @ViewChild('invalidIbanModal') invalidIbanModal: PrModalConfirmComponent;
  products: Product[] = [];
  productsType: ProductsType[] = [];
  currentPageNumber = 0;
  itemsPerPage = 1;
  pledge: PledgeRead;
  sponsors: any[] = [];
  searchingSponsors = false;
  sponsorsSearched = false;
  sponsorIbans: { [productIndex: number]: any[] } = {};
  private discountTypeCache: { [productIndex: number]: { [type: string]: any } } = {};
  protected readonly EaDisplay = EaDisplay;
  @ViewChild('linearVerticalStepper', { static: false })
  private steper: MatStepper;

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    private parrainageService: ParrainageService,
    private cdr: ChangeDetectorRef,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    this.pledge = this.subscriptionFacade.getPledge();
    this.products = this.pledge?.products;

    this.initForm(this.pledge.products);

    // Initialize sponsorIbans from existing data if sponsor was already selected
    this.products.forEach((product, index) => {
      const parametersGroup = this.scpis?.get(`${index}`)?.get('parameters');
      const sponsorEmail = parametersGroup?.get('sponsorEmail')?.value;
      const discountType = parametersGroup?.get('discountType')?.value;

      if (sponsorEmail && discountType === 'PARRAINAGE') {
        // Search for the sponsor to get their IBANs
        this.parrainageService
          .searchSponsors(sponsorEmail)
          .subscribe(sponsors => {
            if (sponsors && sponsors.length > 0) {
              const sponsor = sponsors[0];
              if (sponsor.iban && sponsor.iban.length > 0) {
                const sponsorFullName =
                  sponsor.firstName + ' ' + sponsor.lastName;
                this.sponsorIbans[index] = sponsor.iban.map(iban => ({
                  ...iban,
                  holder: sponsorFullName,
                }));
                this.cdr.markForCheck();
              }
            }
          });
      }
    });

    this.cdr.markForCheck();

    this.setupSponsorSearch();
    this.setupFormListeners();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  setupSponsorSearch() {
    this.products.forEach((product, index) => {
      const sponsorEmailControl = this.scpis
        .get(`${index}`)
        ?.get('parameters')
        ?.get('sponsorEmail');

      if (sponsorEmailControl) {
        sponsorEmailControl.valueChanges
          .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
          .subscribe(value => {
            if (typeof value === 'string' && value.length >= 3) {
              this.searchingSponsors = true;
              this.sponsorsSearched = false;
              this.cdr.markForCheck();

              this.parrainageService.searchSponsors(value).subscribe(
                sponsors => {
                  this.sponsors = sponsors || [];
                  this.searchingSponsors = false;
                  this.sponsorsSearched = true;
                  this.cdr.markForCheck();
                },
                error => {
                  this.sponsors = [];
                  this.searchingSponsors = false;
                  this.sponsorsSearched = true;
                  this.cdr.markForCheck();
                },
              );
            } else if (typeof value === 'object' && value !== null) {
              this.onSponsorSelected(value, index);
            } else {
              this.sponsors = [];
              this.searchingSponsors = false;
              this.sponsorsSearched = false;
              this.clearSponsorData(index);
              this.cdr.markForCheck();
            }
          });
      }
    });
  }

  private getParametersGroup(index: number) {
    return this.scpis.get(`${index}`)?.get('parameters');
  }

  private clearSponsorData(index: number) {
    const parametersGroup = this.getParametersGroup(index);
    parametersGroup?.get('sponsorId')?.setValue(null, { emitEvent: false });
    parametersGroup?.get('discountEmail')?.setValue(null, { emitEvent: false });
    parametersGroup?.get('discountFirstname')?.setValue(null, { emitEvent: false });
    parametersGroup?.get('discountLastname')?.setValue(null, { emitEvent: false });
    this.resetDiscountIban(index);
  }

  private resetDiscountIban(index: number) {
    const parametersGroup = this.getParametersGroup(index);
    parametersGroup?.get('discountIban')?.reset(
      { id: null, iban: null, bic: null, holder: null, bankName: null },
      { emitEvent: false },
    );
    delete this.sponsorIbans[index];
  }

  private saveDiscountTypeState(index: number, type: string) {
    if (!type) return;
    const parametersGroup = this.getParametersGroup(index);
    if (!this.discountTypeCache[index]) {
      this.discountTypeCache[index] = {};
    }

    const iban = parametersGroup?.get('discountIban')?.value;
    const common = {
      discountAmount: parametersGroup?.get('discountAmount')?.value,
      discountIban: iban ? { ...iban } : null,
    };

    if (type === 'PARRAINAGE') {
      this.discountTypeCache[index][type] = {
        ...common,
        sponsorEmail: parametersGroup?.get('sponsorEmail')?.value,
        sponsorId: parametersGroup?.get('sponsorId')?.value,
        sponsorIbans: this.sponsorIbans[index] || [],
      };
    } else if (type === 'APPORTEUR') {
      this.discountTypeCache[index][type] = {
        ...common,
        contributorEmail: parametersGroup?.get('contributorEmail')?.value,
        contributorFirstName: parametersGroup?.get('contributorFirstName')?.value,
        contributorLastName: parametersGroup?.get('contributorLastName')?.value,
      };
    } else {
      this.discountTypeCache[index][type] = common;
    }
  }

  private restoreDiscountTypeState(index: number, type: string) {
    const cached = this.discountTypeCache[index]?.[type];
    const parametersGroup = this.getParametersGroup(index);

    if (!cached) {
      this.resetDiscountIban(index);
      return;
    }

    parametersGroup?.get('discountAmount')?.setValue(cached.discountAmount, { emitEvent: false });
    parametersGroup?.get('discountIban')?.patchValue(cached.discountIban || {}, { emitEvent: false });

    if (type === 'PARRAINAGE') {
      parametersGroup?.get('sponsorEmail')?.setValue(cached.sponsorEmail, { emitEvent: false });
      parametersGroup?.get('sponsorId')?.setValue(cached.sponsorId, { emitEvent: false });
      this.sponsorIbans[index] = cached.sponsorIbans || [];
    } else if (type === 'APPORTEUR') {
      parametersGroup?.get('contributorEmail')?.setValue(cached.contributorEmail, { emitEvent: false });
      parametersGroup?.get('contributorFirstName')?.setValue(cached.contributorFirstName, { emitEvent: false });
      parametersGroup?.get('contributorLastName')?.setValue(cached.contributorLastName, { emitEvent: false });
    }
  }

  onSponsorSelected(sponsor: any, index: number) {
    if (sponsor && sponsor.id) {
      const parametersGroup = this.scpis.get(`${index}`)?.get('parameters');

      parametersGroup?.get('sponsorId')?.setValue(sponsor.id);

      parametersGroup
        ?.get('discountEmail')
        ?.setValue(sponsor.login, { emitEvent: false });
      parametersGroup
        ?.get('discountFirstname')
        ?.setValue(sponsor.firstName, { emitEvent: false });
      parametersGroup
        ?.get('discountLastname')
        ?.setValue(sponsor.lastName, { emitEvent: false });

      // Store all sponsor IBANs for this product
      if (sponsor.iban && sponsor.iban.length > 0) {
        // Enrich sponsor IBANs with holder name
        const sponsorFullName = sponsor.firstName + ' ' + sponsor.lastName;
        this.sponsorIbans[index] = sponsor.iban.map(iban => ({
          ...iban,
          holder: sponsorFullName,
        }));

        // Pre-fill with first IBAN
        const ibanGroup = parametersGroup?.get('discountIban');
        ibanGroup?.patchValue(
          {
            id: sponsor.iban[0].id,
            iban: sponsor.iban[0].iban,
            bic: sponsor.iban[0].bic,
            holder: sponsorFullName,
            bankName: sponsor.iban[0].bankName,
          },
          { emitEvent: true },
        );
      } else {
        this.sponsorIbans[index] = [];
        this.notificationService.warn(
          'IBAN indisponible',
          'Aucun IBAN trouvé pour ce parrain. Veuillez saisir les informations bancaires manuellement.',
        );
      }

      this.cdr.markForCheck();
    }
  }

  submitForm() {
    this.subscriptionFacade.submitPledge(
      this,
      'products',
      this.currentPageNumber === this.products.length - 1,
      this.nextStep.bind(this),
      true,
    );
  }

  onSubmit() {
    this.subscriptionFacade.selectErrorInputAndScroll(this);
    this.form.markAsDirty();
    this.displayErrors();

    if (this.form.valid) {
      this.submitForm();
    }
  }

  setupFormListeners() {
    setTimeout(() => {
      this.products.forEach((product, index) => {
        const parametersGroup = this.scpis?.get(`${index}`)?.get('parameters');
        if (!parametersGroup) {
          return;
        }

        const hasDiscountControl = parametersGroup.get('hasDiscount');
        if (hasDiscountControl) {
          hasDiscountControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              if (!value) {
                parametersGroup.get('discountType')?.setValue(null, { emitEvent: false });
                this.resetDiscountIban(index);
                parametersGroup.get('sponsorEmail')?.setValue(null, { emitEvent: false });
                parametersGroup.get('sponsorId')?.setValue(null, { emitEvent: false });
                parametersGroup.get('contributorEmail')?.setValue(null, { emitEvent: false });
                parametersGroup.get('contributorFirstName')?.setValue(null, { emitEvent: false });
                parametersGroup.get('contributorLastName')?.setValue(null, { emitEvent: false });
                delete this.discountTypeCache[index];
              }
              this.cdr.markForCheck();
            });
        }

        const paymentTypeControl = parametersGroup.get('paymentType');
        if (paymentTypeControl) {
          paymentTypeControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              parametersGroup.reset(
                {
                  holderIsSubscriber: true,
                  productId: this.products?.[index]?.productId,
                  productName: this.products?.[index]?.productName,
                  paymentType: value,
                },
                { emitEvent: false },
              );
            });
        }

        const holderIsSubscriberControl =
          parametersGroup.get('holderIsSubscriber');
        if (holderIsSubscriberControl) {
          holderIsSubscriberControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              if (value) {
                parametersGroup.get('interventionReason')?.setValue(null);
                parametersGroup.get('reltionshipHolderSubscriber')?.setValue(null);

                parametersGroup.get('reltionshipHolderSubscriber')?.clearValidators();
                parametersGroup.get('reltionshipHolderSubscriber')?.updateValueAndValidity();

                parametersGroup.get('interventionReason')?.clearValidators();
                parametersGroup.get('interventionReason')?.updateValueAndValidity();
              }
            });
        }

        const discountTypeControl = parametersGroup.get('discountType');

        if (discountTypeControl) {
          let previousDiscountType = discountTypeControl.value;
          discountTypeControl.valueChanges
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe(value => {
              this.saveDiscountTypeState(index, previousDiscountType);
              this.restoreDiscountTypeState(index, value);
              previousDiscountType = value;
              this.cdr.markForCheck();
            });
        }

        const sponsorEmailControl = parametersGroup.get('sponsorEmail');
        if (sponsorEmailControl) {
          sponsorEmailControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              const discountType = parametersGroup.get('discountType')?.value;
              if (discountType === 'PARRAINAGE') {
                const email =
                  typeof value === 'object' && value !== null
                    ? value.login
                    : value;
                parametersGroup.get('discountEmail')?.setValue(email, { emitEvent: false });
              }
            });
        }

        const contributorEmailControl = parametersGroup.get('contributorEmail');
        if (contributorEmailControl) {
          contributorEmailControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              const discountType = parametersGroup.get('discountType')?.value;
              if (discountType === 'APPORTEUR') {
                parametersGroup.get('discountEmail')?.setValue(value, { emitEvent: false });
              }
            });
        }

        const contributorFirstNameControl = parametersGroup.get(
          'contributorFirstName',
        );
        if (contributorFirstNameControl) {
          contributorFirstNameControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              const discountType = parametersGroup.get('discountType')?.value;
              if (discountType === 'APPORTEUR') {
                parametersGroup.get('discountFirstname')?.setValue(value, { emitEvent: false });
              }
            });
        }

        const contributorLastNameControl = parametersGroup.get(
          'contributorLastName',
        );
        if (contributorLastNameControl) {
          contributorLastNameControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
              const discountType = parametersGroup.get('discountType')?.value;
              if (discountType === 'APPORTEUR') {
                parametersGroup.get('discountLastname')?.setValue(value, { emitEvent: false });
              }
            });
        }
      });
    }, 0);
  }

  nextPage() {
    this.currentPageNumber++;
  }

  prev() {
    if (this.currentPageNumber === 0) {
      this.subscriptionFacade.previousStep();
    }
    if (this.currentPageNumber > 0) {
      this.currentPageNumber--;
    }
    this.steper.previous();
  }

  nextStep() {
    if (this.currentPageNumber < this.products.length - 1) {
      this.currentPageNumber++;
      this.steper.next();
      this.steper.selected.completed = true;
    } else if (this.currentPageNumber === this.products.length - 1) {
      this.steper.selected.completed = true;
    }
  }

  prevStep() {
    if (this.currentPageNumber === 0) {
      this.subscriptionFacade.previousStep();
      this.steper.previous();
    }
    if (this.currentPageNumber > 0) {
      this.currentPageNumber--;
    }
  }

  stepClick(ev) {
    this.currentPageNumber = ev.selectedIndex;
  }

  onConfirmInvalidIban() {
    this.submitForm();
  }

  onCancelInvalidIban() {
    this.invalidIbanModal.close();
  }
}
